import { type RequestPayload, router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Checkbox, Divider, Input, Group as MantineGroup, NumberInput, Textarea, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { isNotEmpty, useForm } from "@mantine/form";
import { type ReactNode, useState } from "react";
import { notEmptyMessage } from "../../../helpers/formHelper";
import { getFullName } from "../../../helpers/stringHelper";
import type { Occupation } from "../../../models/enums/occupation";
import { ReportType } from "../../../models/enums/reportType";
import { Role } from "../../../models/enums/role";
import type { Group } from "../../../models/group";
import { appendUUID } from "../../../models/IDElement";
import {
  type CertificateConfiguration,
  emptyProjectDetails,
  emptySubject,
  emptyTechnicalEducation,
  emptyUeKModule,
  type ProjectDetails,
  type Subject,
  type TechnicalEducation,
  type UeKModule,
} from "../../../models/reportConfigs/certificateConfiguration";
import type { Signature } from "../../../models/signature";
import type { YearContext } from "../../../models/yearContext";
import { AccessConfigForm, type AccessConfigFormType } from "../../accessConfig/AccessConfigForm";
import { SaveButton } from "../../button/SaveButton";
import { emailTemplateValidation } from "../../email/EmailEditorForm";
import { FilterSelect } from "../../select/FilterSelect";
import { ManagableList } from "../../sortable/ManagableList";
import { BaseConfigForm, type BaseConfigFormType } from "./BaseConfigForm";

type CertificateFormProps = {
  certificateConfig?: CertificateConfiguration;
  saveButtonLabel: string;
  yearContexts: YearContext[];
  groups: Group[];
  onSubmit: (values: RequestPayload) => void;
  reportType: ReportType.CERT | ReportType.JUMP;
  alert?: ReactNode;
  signatures: Signature[];
};

type FormErrors = {
  title?: string[];
  data?: string[];
  report_type?: string[];
};

type CertificateFormType = AccessConfigFormType &
  BaseConfigFormType & {
    report_type: ReportType;
    data: {
      starts_at: string | null;
      ends_at: string | null;
      project: boolean;
      uek_modules: UeKModule[];
      technical_education: TechnicalEducation[];
      project_details: ProjectDetails;
      first_signature: string | null;
      second_signature: string | null;
    };
  };

export function CertificateConfigForm({
  certificateConfig,
  saveButtonLabel,
  onSubmit,
  groups,
  yearContexts,
  reportType,
  alert,
  signatures,
}: CertificateFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;
  const isCertificate = reportType === ReportType.CERT;
  const [showProjectDetails, setShowProjectDetails] = useState(isCertificate && (certificateConfig?.data.project ?? false));
  const signatureOptions = signatures.map((s) => ({ label: getFullName(s), value: s.id.toString() }));

  const form = useForm<CertificateFormType>({
    mode: "uncontrolled",
    initialValues: {
      report_type: certificateConfig?.report_type ?? reportType,
      title: certificateConfig?.title,
      group_id: certificateConfig?.group?.id.toString(),
      year_context_id: certificateConfig?.year_context?.id.toString(),
      occupation: certificateConfig?.occupation,
      document_readable: certificateConfig?.document_readable ?? [Role.APPRENTICE, Role.APPRENTICE_SUPERVISOR, Role.PARENT],
      document_signable: certificateConfig?.document_signable ?? [],
      email_template: certificateConfig?.email_template,
      send_mail: certificateConfig?.send_mail ?? true,
      subject: certificateConfig?.subject,
      data: {
        starts_at: certificateConfig?.data.starts_at ?? null,
        ends_at: certificateConfig?.data.ends_at ?? null,
        project: isCertificate ? (certificateConfig?.data.project ?? false) : false,
        uek_modules: certificateConfig?.data.uek_modules ?? [appendUUID(emptyUeKModule)],
        technical_education: certificateConfig?.data.technical_education ?? [appendUUID(emptyTechnicalEducation)],
        project_details: certificateConfig?.data.project_details ?? {
          ...emptyProjectDetails,
          subject: [appendUUID(emptySubject)],
        },
        first_signature: certificateConfig?.data.first_signature?.toString() ?? null,
        second_signature: certificateConfig?.data.second_signature?.toString() ?? null,
      },
    },
    validate: {
      title: isNotEmpty(notEmptyMessage),
      subject: (value, values) => (values.send_mail ? isNotEmpty(notEmptyMessage)(value) : null),
      email_template: (value, values) => (values.send_mail ? emailTemplateValidation(value) : null),
      data: {
        starts_at: isNotEmpty(notEmptyMessage),
        ends_at: isNotEmpty(notEmptyMessage),
        uek_modules: {
          num: isNotEmpty(notEmptyMessage),
          name: isNotEmpty(notEmptyMessage),
        },
        technical_education: {
          title: isNotEmpty(notEmptyMessage),
        },
        project_details: {
          subject: {
            title: isCertificate ? isNotEmpty(notEmptyMessage) : undefined,
            max_points: isCertificate ? isNotEmpty(notEmptyMessage) : undefined,
          },
          total_max_points: isCertificate ? isNotEmpty(notEmptyMessage) : undefined,
        },
        first_signature: isNotEmpty(notEmptyMessage),
        second_signature: isNotEmpty(notEmptyMessage),
      },
    },
  });

  form.watch("year_context_id", ({ value }) => reloadGroups(value, form.getValues().occupation));
  form.watch("occupation", ({ value }) => reloadGroups(form.getValues().year_context_id, value));
  form.watch("data.project", ({ value }) => setShowProjectDetails(value ? isCertificate && value : false));
  const reloadGroups = (yearContextId?: string, occupation?: Occupation) =>
    router.reload({
      only: ["groups"],
      data: { selected_year_context_id: yearContextId, selected_occupation: occupation },
    });

  const setTotalPoints = () => {
    const max = form.getValues().data.project_details.subject.reduce((prev, curr) => prev + (curr.max_points ?? 0), 0);
    form.setFieldValue("data.project_details.total_max_points", max);
  };

  const renderUekModuleInput = (index: number) => (
    <>
      <NumberInput
        placeholder="ÜK Nummer"
        flex={1}
        min={0}
        key={form.key(`data.uek_modules.${index}.num`)}
        {...form.getInputProps(`data.uek_modules.${index}.num`)}
      />
      <Textarea
        placeholder="Name"
        flex={3}
        resize="vertical"
        minRows={1}
        key={form.key(`data.uek_modules.${index}.name`)}
        {...form.getInputProps(`data.uek_modules.${index}.name`)}
      />
    </>
  );

  const uekModuleLabels = (
    <>
      <Input.Wrapper flex={1} label="ÜK Nummer" withAsterisk />
      <Input.Wrapper flex={3} label="Name" withAsterisk />
    </>
  );

  const renderTechnicalEducationInput = (index: number) => (
    <>
      <TextInput
        flex={1}
        placeholder="Titel"
        key={form.key(`data.technical_education.${index}.title`)}
        {...form.getInputProps(`data.technical_education.${index}.title`)}
      />
      <Textarea
        flex={3}
        placeholder="Beschreibung"
        resize="vertical"
        minRows={1}
        key={form.key(`data.technical_education.${index}.details`)}
        {...form.getInputProps(`data.technical_education.${index}.details`)}
      />
    </>
  );

  const technicalEducationLabels = (
    <>
      <Input.Wrapper flex={1} label="Titel" withAsterisk />
      <Input.Wrapper flex={3} label="Beschreibung" />
    </>
  );

  const renderProjectDetails = (index: number) => (
    <>
      <TextInput
        flex={2}
        placeholder="Bereich"
        key={form.key(`data.project_details.subject.${index}.title`)}
        {...form.getInputProps(`data.project_details.subject.${index}.title`)}
      />
      <NumberInput
        flex={1}
        min={0}
        placeholder="Punkte"
        key={form.key(`data.project_details.subject.${index}.max_points`)}
        {...form.getInputProps(`data.project_details.subject.${index}.max_points`)}
        onBlur={(event) => {
          /* It's not possible to watch on an array in mantine so this is a workaround */
          setTotalPoints();
          form.getInputProps(`data.project_details.subject.${index}.max_points`).onBlur(event);
        }}
      />
    </>
  );

  const projectSubjectLabels = (
    <>
      <Input.Label flex={2}>Bereich</Input.Label>
      <Input.Label flex={1}>Punkte</Input.Label>
    </>
  );

  return (
    <BaseConfigForm
      alert={alert}
      form={form}
      errors={errors}
      onSubmit={onSubmit}
      groups={groups}
      yearContexts={yearContexts}
      saveButtonLabel={saveButtonLabel}
    >
      <MantineGroup>
        <DatePickerInput
          flex={1}
          clearable
          allowDeselect
          withAsterisk
          label="Beginnt am"
          valueFormat="DD.MM.YYYY"
          key={form.key("data.starts_at")}
          {...form.getInputProps("data.starts_at")}
        />
        <DatePickerInput
          flex={1}
          clearable
          allowDeselect
          withAsterisk
          label="Endet am"
          valueFormat="DD.MM.YYYY"
          key={form.key("data.ends_at")}
          {...form.getInputProps("data.ends_at")}
        />
      </MantineGroup>
      <ManagableList
        defaultProps={emptyUeKModule}
        onAdd={(uek: UeKModule) => form.insertListItem("data.uek_modules", uek)}
        onDelete={(index: number) => form.removeListItem("data.uek_modules", index)}
        maw="60rem"
        label="ÜK Module"
        reorderListItem={(from, to) => form.reorderListItem("data.uek_modules", { from, to })}
        itemRender={renderUekModuleInput}
        values={form.values.data.uek_modules}
        detailLabels={uekModuleLabels}
      />
      <ManagableList
        defaultProps={emptyTechnicalEducation}
        onAdd={(technical: TechnicalEducation) => form.insertListItem("data.technical_education", technical)}
        onDelete={(index: number) => form.removeListItem("data.technical_education", index)}
        maw="60rem"
        label="Fachliche Ausbildung"
        reorderListItem={(from, to) => form.reorderListItem("data.technical_education", { from, to })}
        itemRender={renderTechnicalEducationInput}
        detailLabels={technicalEducationLabels}
        values={form.values.data.technical_education}
      />
      {isCertificate && (
        <Checkbox label="Projektarbeit" key={form.key("data.project")} {...form.getInputProps("data.project", { type: "checkbox" })} />
      )}
      {showProjectDetails && (
        <>
          <ManagableList
            defaultProps={emptySubject}
            onAdd={(sub: Subject) => form.insertListItem("data.project_details.subject", sub)}
            onDelete={(index: number) => form.removeListItem("data.project_details.subject", index)}
            maw="60rem"
            label="Projekt"
            reorderListItem={(from, to) => form.reorderListItem("data.project_details.subject", { from, to })}
            itemRender={renderProjectDetails}
            detailLabels={projectSubjectLabels}
            values={form.values.data.project_details?.subject ?? []}
          />
          <NumberInput
            disabled
            flex={1}
            label="Totale Punkte"
            key={form.key("data.project_details.total_max_points")}
            {...form.getInputProps("data.project_details.total_max_points")}
          />
        </>
      )}
      <Divider />
      <MantineGroup>
        <FilterSelect
          flex={1}
          clearable
          withAsterisk
          label="Linke Unterschrift"
          data={signatureOptions}
          key={form.key("data.first_signature")}
          {...form.getInputProps("data.first_signature")}
        />
        <FilterSelect
          flex={1}
          withAsterisk
          label="Rechte Unterschrift"
          clearable
          data={signatureOptions}
          key={form.key("data.second_signature")}
          {...form.getInputProps("data.second_signature")}
        />
      </MantineGroup>
      <AccessConfigForm form={form} />
      <Divider />
      <SaveButton label={saveButtonLabel} />
    </BaseConfigForm>
  );
}
